import React, { useState, useEffect } from 'react';

export interface ToastMessage {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  duration?: number;
}

interface ToastProps {
  messages: ToastMessage[];
  onRemove: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ messages, onRemove }) => {
  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];

    messages.forEach((msg) => {
      const timer = setTimeout(() => {
        onRemove(msg.id);
      }, msg.duration || 3000);
      timers.push(timer);
    });

    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, [messages, onRemove]);

  const getBackgroundColor = (type: ToastMessage['type']) => {
    switch (type) {
      case 'success': return 'var(--color-success, #2E7D32)';
      case 'error': return 'var(--color-danger, #B71C1C)';
      case 'info': return 'var(--color-info, #1565C0)';
      case 'warning': return 'var(--color-warning, #E65100)';
      default: return 'var(--color-secondary, #616161)';
    }
  };

  const getIcon = (type: ToastMessage['type']) => {
    switch (type) {
      case 'success': return 'fa-check-circle';
      case 'error': return 'fa-times-circle';
      case 'info': return 'fa-info-circle';
      case 'warning': return 'fa-exclamation-triangle';
      default: return 'fa-bell';
    }
  };

  if (messages.length === 0) return null;

  return (
    <div style={{
      position: 'fixed',
      top: '110px',  // Just below 100px header
      right: '20px',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    }}>
      {messages.map((msg) => (
        <div
          key={msg.id}
          style={{
            backgroundColor: getBackgroundColor(msg.type),
            color: 'white',
            padding: '8px 12px',  // Smaller padding
            borderRadius: '6px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            minWidth: '200px',  // Smaller minimum width
            maxWidth: '350px',  // Smaller maximum width
            animation: 'slideInRight 0.2s ease-out',  // Faster animation
            cursor: 'pointer',
            opacity: 0.95  // Slightly transparent
          }}
          onClick={() => onRemove(msg.id)}
        >
          <i className={`fas ${getIcon(msg.type)}`} style={{ fontSize: '14px' }}></i>
          <span style={{ flex: 1, fontSize: '12px', fontWeight: '500', lineHeight: '1.4' }}>{msg.message}</span>
          <i className="fas fa-times" style={{ fontSize: '12px', opacity: 0.7 }}></i>
        </div>
      ))}
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default Toast;